import React, { useRef, useState } from 'react';
import { Button, Card, Empty, Flex, Input, Spin, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Tag from '@/components/Tag';
import { queryUserLeads } from '@/services/member';
import { MemberStatus, SearchUserInfo, UserStageType } from '@/types/member';
import { getMemberStatusLabel } from '../utils';
import drawerStyles from '../index.less';
import styles from './StepSearchUser.less';

interface IProps {
  onSelectUser: (user: SearchUserInfo) => void;
  onCreateClue: (keyword: string) => void;
}

export default function StepSearchUser({ onSelectUser, onCreateClue }: IProps) {
  const [searchValue, setSearchValue] = useState('');
  const [fetching, setFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [options, setOptions] = useState<SearchUserInfo[]>([]);
  const fetchRef = useRef(0);

  async function handleSearch() {
    const keyword = searchValue.trim();
    if (!keyword) {
      message.error('请输入手机号或用户名');
      return;
    }

    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setFetching(true);
    setHasSearched(true);
    setOptions([]);

    try {
      const res = await queryUserLeads({
        search_types: [UserStageType.USER],
        keywords: keyword,
      });
      if (fetchId !== fetchRef.current) {
        return;
      }
      setOptions(res.user || []);
    } catch (error) {
      setOptions([]);
    } finally {
      setFetching(false);
    }
  }

  const showEmpty = hasSearched && !fetching && options.length === 0;

  return (
    <div>
      <Typography.Title level={4} className={drawerStyles.screenTitle}>
        选择用户
      </Typography.Title>

      <Flex align="center" gap={12} style={{ marginBottom: 18 }}>
        <Input
          size="middle"
          style={{ flex: 1 }}
          placeholder="输入手机号或用户名搜索"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onPressEnter={handleSearch}
          autoFocus
          suffix={fetching ? <Spin size="small" /> : null}
        />
        <Button
          type="primary"
          size="middle"
          icon={<SearchOutlined />}
          loading={fetching}
          onClick={handleSearch}
        >
          搜索
        </Button>
      </Flex>

      {options.length > 0 && (
        <div className={styles.resultList}>
          {options.map((user) => {
            const status = getMemberStatusLabel(user.is_member as MemberStatus);
            return (
              <Card key={user.user_id} bordered className={styles.resultCard}>
                <div>
                  <div className={styles.resultTitle}>
                    <span className={styles.resultTitleName}>{user.name}</span>
                    <Tag color={status.color}>{status.text}</Tag>
                  </div>
                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>手机号</span>
                      {user.phone_number || '-'}
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>归属门店</span>
                      {user.store_name || '-'}
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>当前负责人</span>
                      {user.admin_name || '-'}
                    </div>
                  </div>
                </div>
                <Button type="primary" onClick={() => onSelectUser(user)}>
                  为该用户开卡
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {showEmpty && (
        <div className={styles.emptyWrapper}>
          <Empty
            description={
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16 }}>
                  未找到匹配用户
                </Typography.Text>
                <Typography.Text type="secondary">
                  录入一条线索，线索保存后，直接为该用户开卡。
                </Typography.Text>
              </div>
            }
          >
            <Button type="primary" onClick={() => onCreateClue(searchValue.trim())}>
              新建线索并开卡
            </Button>
          </Empty>
        </div>
      )}
    </div>
  );
}
